import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import SEO from '../../components/SEO';
import ContactSection from '../../components/ContactSection';
import { BookOpen, ArrowRight, Calendar, Tag } from 'lucide-react';

const blogPosts = [
  {
    title: 'Matterport for Architects: How 3D Scanning Is Transforming Architectural Practice',
    excerpt: 'How architects across London and the UK are using Matterport 3D scanning for as-built surveys, CAD/BIM integration, client presentations, design verification and site surveys without travel.',
    date: '2026-05-05',
    category: 'Architecture',
    slug: '/blog/matterport-for-architects',
    readTime: '9 min read',
  },
  {
    title: 'Virtual Tours for Commercial Property: Reducing Void Periods and Transforming Marketing',
    excerpt: 'How commercial property agents and landlords use 3D virtual tours to reduce void periods, enable remote viewings, and improve marketing ROI across London and the UK.',
    date: '2026-05-05',
    category: 'Commercial Property',
    slug: '/blog/virtual-tours-commercial-property',
    readTime: '9 min read',
  },
  {
    title: 'Matterport vs Laser Scanning: Which Do You Need?',
    excerpt: 'A detailed comparison of Matterport and traditional laser scanning. Understand accuracy levels, cost, speed, use cases for each, and when to combine both technologies.',
    date: '2026-05-05',
    category: 'Technology',
    slug: '/blog/matterport-vs-laser-scanning-which-do-you-need',
    readTime: '10 min read',
  },
  {
    title: 'What Is a Digital Twin? A Complete Guide for Construction and FM',
    excerpt: 'Discover how digital twins are transforming construction and facilities management. Learn what they are, how they work, and why leading organisations are adopting them.',
    date: '2026-04-28',
    category: 'Technology',
    slug: '/blog/what-is-a-digital-twin',
    readTime: '8 min read',
  },
  {
    title: 'How Much Does a Matterport Scan Cost in the UK?',
    excerpt: 'A transparent breakdown of Matterport 3D scanning costs in the UK. Understand pricing factors, what is included, and how to calculate your return on investment.',
    date: '2026-04-14',
    category: 'Pricing',
    slug: '/blog/how-much-does-matterport-scan-cost-uk',
    readTime: '7 min read',
  },
  {
    title: '3D Scanning for NHS Hospitals: Virtual Tours and Estate Management',
    excerpt: 'How NHS trusts are using 3D scanning for neonatal virtual tours, estates management, infection control documentation, and FM compliance.',
    date: '2026-03-30',
    category: 'Healthcare',
    slug: '/blog/3d-scanning-nhs-hospitals',
    readTime: '9 min read',
  },
  {
    title: 'Drone Survey Costs in the UK: What to Expect in 2026',
    excerpt: 'A comprehensive guide to drone survey pricing in the UK, covering topographic surveys, construction progress monitoring, thermal imaging, and CAA regulations.',
    date: '2026-03-15',
    category: 'Drone Surveys',
    slug: '/blog/drone-survey-costs-uk',
    readTime: '8 min read',
  },
  {
    title: 'BIM Level 2 Requirements in the UK: What You Need to Know',
    excerpt: 'Understand the UK government BIM mandate, ISO 19650 requirements, and how scan-to-BIM services help you achieve compliance on public sector projects.',
    date: '2026-03-01',
    category: 'BIM',
    slug: '/blog/bim-level-2-requirements-uk',
    readTime: '9 min read',
  },
];

const categoryColours = {
  Technology: 'bg-purple-100 text-purple-700',
  Pricing: 'bg-green-100 text-green-700',
  Healthcare: 'bg-red-100 text-red-700',
  'Drone Surveys': 'bg-amber-100 text-amber-700',
  BIM: 'bg-blue-100 text-blue-700',
  Architecture: 'bg-blue-100 text-blue-700',
  'Commercial Property': 'bg-green-100 text-green-700',
};

const BlogIndexPage = () => {
  const structuredData = [
    {
      '@context': 'https://schema.org',
      '@type': 'Blog',
      name: 'Digital Scanning Group Blog',
      description: 'Expert insights on 3D scanning, digital twins, BIM, drone surveys, and Matterport technology for construction, healthcare, and facilities management.',
      url: 'https://www.digitalscanninggroup.com/blog',
      publisher: {
        '@type': 'Organization',
        name: 'Digital Scanning Group',
        url: 'https://www.digitalscanninggroup.com',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.digitalscanninggroup.com' },
        { '@type': 'ListItem', position: 2, name: 'Blog', item: 'https://www.digitalscanninggroup.com/blog' },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Blog | 3D Scanning Insights and Guides"
        description="Expert articles on 3D scanning, digital twins, Matterport, BIM, and drone surveys for construction and facilities management. Learn more."
        keywords="3D scanning blog, digital twin guide, matterport blog, BIM articles, drone survey guide, construction technology blog"
        canonicalUrl={typeof window !== 'undefined' ? `${window.location.origin}/blog` : ''}
        structuredData={structuredData}
      />
      <Navbar scrolled={true} />

      {/* Hero */}
      <section className="relative pt-32 pb-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-blue-50 rounded-full mb-6">
              <BookOpen className="h-6 w-6 text-blue-600" />
              <span className="text-blue-600 font-semibold">INSIGHTS AND GUIDES</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              The DSG Blog
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Expert insights on 3D scanning, digital twins, BIM, drone surveys, and reality capture technology. Practical guides for construction, healthcare, and facilities management professionals.
            </p>
          </div>

          {/* Blog Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
            {blogPosts.map((post, index) => (
              <Link
                key={index}
                to={post.slug}
                className="group bg-white border-2 border-gray-100 rounded-2xl p-8 hover:border-blue-200 hover:shadow-lg transition-all duration-300"
              >
                <div className="flex items-center gap-3 mb-4">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${categoryColours[post.category] || 'bg-gray-100 text-gray-700'}`}>
                    <Tag className="inline h-3 w-3 mr-1" />
                    {post.category}
                  </span>
                  <span className="text-sm text-gray-400">{post.readTime}</span>
                </div>
                <h2 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">
                  {post.title}
                </h2>
                <p className="text-gray-600 mb-4 leading-relaxed">
                  {post.excerpt}
                </p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm text-gray-400">
                    <Calendar className="h-4 w-4" />
                    {new Date(post.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </div>
                  <span className="flex items-center gap-1 text-blue-600 font-medium text-sm group-hover:gap-2 transition-all">
                    Read <ArrowRight className="h-4 w-4" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <ContactSection />
      <Footer />
    </div>
  );
};

export default BlogIndexPage;
